import { authHeaders } from './credentials.js';
import { RedesignError, request } from './errors.js';
import type {
  RedesignInput,
  RedesignResult,
  RedesignRunOptions,
  RedesignTarget,
} from './types.js';

export type BodyMode = 'multipart' | 'json-base64';

export interface HttpApiConfig {
  id?: string;
  label?: string;
  endpoint: string;
  apiKey?: string;
  authHeader?: string;
  authPrefix?: string;
  bodyMode: BodyMode;
  /** Field names the endpoint expects. Omit one to leave that value out. */
  fields: {
    image?: string;
    mask?: string;
    prompt?: string;
    negativePrompt?: string;
    seed?: string;
    width?: string;
    height?: string;
  };
  /** Constant fields merged into every request (model name, size, steps...). */
  extra?: Record<string, string | number | boolean>;
  extraHeaders?: Record<string, string>;
  /**
   * Where the image sits in a JSON response, e.g. `data[].b64_json`.
   * Ignored when the response is an image already.
   */
  responsePath?: string;
  responseEncoding?: 'base64' | 'url';
  /** MIME type assumed for base64 payloads that do not carry a data URL prefix. */
  responseMime?: string;
}

/**
 * A configurable image-generation endpoint.
 *
 * Every provider has its own field names and response shape, and hard-coding one
 * would mean a code change to switch. This adapter is driven by configuration -
 * field names, body encoding, and a path into the JSON response - so a new
 * provider is a settings edit. `API_PRESETS` holds starting points for the
 * common shapes; treat them as templates to check against the provider's own
 * docs rather than as verified integrations.
 */
export class HttpApiTarget implements RedesignTarget {
  readonly id: string;
  readonly label: string;

  constructor(private readonly config: HttpApiConfig) {
    this.id = config.id ?? 'http-api';
    this.label = config.label ?? 'Image API';
  }

  get endpoint(): string {
    return this.config.endpoint;
  }

  async run(input: RedesignInput, options: RedesignRunOptions = {}): Promise<RedesignResult> {
    const { onProgress, signal } = options;
    onProgress?.({ stage: 'uploading', fraction: null, message: 'sending the cut-out' });

    const { body, headers } = await buildRequestBody(this.config, input);
    const response = await request(
      this.config.endpoint,
      {
        method: 'POST',
        headers: {
          ...headers,
          ...this.config.extraHeaders,
          ...authHeaders(this.config.apiKey, this.config.authHeader, this.config.authPrefix),
        },
        body,
        signal,
      },
      'The image API call',
    );

    onProgress?.({ stage: 'downloading', fraction: null, message: 'reading the result' });
    return { images: await readImages(response, this.config, signal) };
  }
}

/** Exposed for testing: builds the request without performing it. */
export async function buildRequestBody(
  config: HttpApiConfig,
  input: RedesignInput,
): Promise<{ body: BodyInit; headers: Record<string, string> }> {
  const { fields, extra } = config;

  if (config.bodyMode === 'multipart') {
    const form = new FormData();
    if (fields.image) form.append(fields.image, input.image, 'image.png');
    if (fields.mask && input.mask) form.append(fields.mask, input.mask, 'mask.png');
    if (fields.prompt && input.prompt !== undefined) form.append(fields.prompt, input.prompt);
    if (fields.negativePrompt && input.negativePrompt !== undefined) {
      form.append(fields.negativePrompt, input.negativePrompt);
    }
    if (fields.seed && input.seed !== undefined) form.append(fields.seed, String(input.seed));
    if (fields.width && input.width !== undefined) form.append(fields.width, String(input.width));
    if (fields.height && input.height !== undefined) form.append(fields.height, String(input.height));
    for (const [key, value] of Object.entries(extra ?? {})) form.append(key, String(value));
    // No content-type header: the browser must set the multipart boundary.
    return { body: form, headers: {} };
  }

  const payload: Record<string, unknown> = { ...extra };
  if (fields.image) payload[fields.image] = await blobToBase64(input.image);
  if (fields.mask && input.mask) payload[fields.mask] = await blobToBase64(input.mask);
  if (fields.prompt && input.prompt !== undefined) payload[fields.prompt] = input.prompt;
  if (fields.negativePrompt && input.negativePrompt !== undefined) {
    payload[fields.negativePrompt] = input.negativePrompt;
  }
  if (fields.seed && input.seed !== undefined) payload[fields.seed] = input.seed;
  if (fields.width && input.width !== undefined) payload[fields.width] = input.width;
  if (fields.height && input.height !== undefined) payload[fields.height] = input.height;

  return {
    body: JSON.stringify(payload),
    headers: { 'content-type': 'application/json' },
  };
}

async function readImages(
  response: Response,
  config: HttpApiConfig,
  signal?: AbortSignal,
): Promise<Blob[]> {
  const contentType = response.headers.get('content-type') ?? '';

  // Some endpoints return the image bytes directly.
  if (contentType.startsWith('image/')) return [await response.blob()];

  const json = (await response.json()) as unknown;
  if (!config.responsePath) {
    throw new RedesignError(
      'The endpoint returned JSON but no response path is configured.',
      `Set the path to the image field, e.g. "data[].b64_json". Response keys: ${describeShape(json)}`,
    );
  }

  const values = getByPath(json, config.responsePath);
  if (values.length === 0) {
    throw new RedesignError(
      `Nothing at "${config.responsePath}" in the response.`,
      `Response keys: ${describeShape(json)}`,
    );
  }

  const blobs: Blob[] = [];
  for (const value of values) {
    if (typeof value !== 'string') continue;
    if (config.responseEncoding === 'url' || /^https?:\/\//.test(value)) {
      const image = await request(value, { signal }, 'Downloading the result');
      blobs.push(await image.blob());
    } else {
      blobs.push(base64ToBlob(value, config.responseMime ?? 'image/png'));
    }
  }
  if (blobs.length === 0) throw new RedesignError('The response held no usable image data.');
  return blobs;
}

/**
 * Reads a dotted path, where `[]` maps over an array.
 *
 * `data[].b64_json` on `{data:[{b64_json:'a'},{b64_json:'b'}]}` yields both.
 * Always returns an array so callers handle single and multi-image responses the
 * same way.
 */
export function getByPath(value: unknown, path: string): unknown[] {
  let current: unknown[] = [value];
  for (const rawSegment of path.split('.')) {
    if (rawSegment.length === 0) continue;
    const isArray = rawSegment.endsWith('[]');
    const key = isArray ? rawSegment.slice(0, -2) : rawSegment;

    const next: unknown[] = [];
    for (const item of current) {
      const child =
        key.length === 0 ? item : (item as Record<string, unknown> | null)?.[key];
      if (child === undefined || child === null) continue;
      if (isArray && Array.isArray(child)) next.push(...child);
      else next.push(child);
    }
    current = next;
  }
  return current;
}

function describeShape(value: unknown): string {
  if (typeof value !== 'object' || value === null) return typeof value;
  return Object.keys(value as Record<string, unknown>).join(', ') || '(empty object)';
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBlob(value: string, mime: string): Blob {
  // Tolerate a data URL prefix, which several providers include.
  const payload = value.startsWith('data:') ? (value.split(',')[1] ?? '') : value;
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Starting points for the common request shapes.
 *
 * These are templates, not verified integrations: providers change field names
 * and response shapes, and the workbench shows every field so they can be
 * corrected without a code change.
 */
export const API_PRESETS: Record<string, Omit<HttpApiConfig, 'endpoint' | 'apiKey'>> = {
  'openai-images-edit': {
    label: 'OpenAI-compatible /images/edits',
    bodyMode: 'multipart',
    fields: { image: 'image', mask: 'mask', prompt: 'prompt' },
    extra: { model: 'gpt-image-1', size: 'auto' },
    responsePath: 'data[].b64_json',
    responseEncoding: 'base64',
  },
  'json-base64': {
    label: 'Generic JSON + base64',
    bodyMode: 'json-base64',
    fields: {
      image: 'image',
      mask: 'mask',
      prompt: 'prompt',
      negativePrompt: 'negative_prompt',
      seed: 'seed',
    },
    responsePath: 'images[]',
    responseEncoding: 'base64',
  },
  'multipart-binary': {
    label: 'Multipart in, image bytes out',
    bodyMode: 'multipart',
    fields: { image: 'image', mask: 'mask', prompt: 'prompt' },
  },
};
